import React from "react";

import {
  createAccordionItems,
  isFootprintReadOnly,
  useGetCarbonProgramByFarmId,
  useGetFootprintingBeefFootprint,
  useNavigate,
  useTranslation,
  useWasAutofilled,
} from "utils";

import { FormFlowScreen, FormPage } from "components";

import { AutofillBanner } from "pages/app/footprinting/components/AutofillBanner";
import { BeefFeedAdditiveForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefFeedAdditiveForm";
import { BeefFeedForageLossForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefFeedForageLossForm";
import { BeefFeedInformationForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefFeedInformationForm";
import { BeefFeedPastureForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefFeedPastureForm";
import { BeefGettingStartedForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefGettingStartedForm";
import { BeefHerdDetailsForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefHerdDetailsForm";
import { BeefHerdOffspringForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefHerdOffspringForm";
import { BeefHerdSizeForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefHerdSizeForm";
import { BeefManureAdditiveForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefManureAdditiveForm";
import { BeefManureStorageForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefManureStorageForm";
import { BeefMoorlandForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefMoorlandForm";
import { BeefYieldForm } from "pages/app/footprinting/components/formFlows/beefFlow/BeefYieldForm";
import { BeefCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

type BeefFlowProps = {
  farmId: string;
  year: number;
  initialStep?: number;
  editMode?: boolean;
  enablePageTransitions?: boolean;
};

const beefFormCategories = {
  animals: "footprinting:footprintingLandingPage.assessmentCategories.beef.animalsBreed",
  feed: "footprinting:footprintingLandingPage.assessmentCategories.beef.feed",
  manure: "footprinting:footprintingLandingPage.assessmentCategories.beef.manure",
};

export const BeefFormFlow = ({ farmId, year, initialStep, editMode, enablePageTransitions }: BeefFlowProps) => {
  const { t } = useTranslation("footprintingBeef");
  const navigate = useNavigate();
  const beefProgressQuery = useGetFootprintingBeefFootprint(farmId, year);
  const readOnly = isFootprintReadOnly(beefProgressQuery.data);
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);
  const wasAutofilled = useWasAutofilled("beef", farmId, year);
  const [currentStep, setCurrentStep] = React.useState<number | undefined>(initialStep ?? 0);

  const getPageStatus = (pageKey: string) => {
    if (beefProgressQuery.data?.pagesCompleted.includes(pageKey)) return "complete";
    if (beefProgressQuery.data?.pagesIncomplete.includes(pageKey)) return "incomplete";
    return null;
  };

  const beefFlowSteps: FormPage[] = [
    {
      navigationTitle: t("footprinting:navigation.beef.gettingStarted"),
      key: BeefGettingStartedForm.Key,
      status: getPageStatus(BeefGettingStartedForm.Key),
      pageSubtitle: t("beefGettingStartedForm.subTitle"),
      body: () => <BeefGettingStartedForm primaryDataId={beefProgressQuery.data?.footprint?.id} />,
      accordionFooter: wasAutofilled ? <AutofillBanner /> : undefined,
    },
    {
      navigationTitle: t("footprinting:navigation.beef.beefYield"),
      key: BeefYieldForm.Key,
      status: getPageStatus(BeefYieldForm.Key),
      pageSubtitle: t("beefYieldForm.subTitle"),
      body: () => <BeefYieldForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingBeef", "beefYield"),
    },
    {
      navigationTitle: t("footprinting:navigation.beef.herdSize"),
      navigationCategory: t(beefFormCategories.animals),
      key: BeefHerdSizeForm.Key,
      status: getPageStatus(BeefHerdSizeForm.Key),
      pageSubtitle: t("beefHerdSizeForm.subTitle", { year }),
      body: () => <BeefHerdSizeForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingBeef", "herdSize"),
    },
    {
      navigationTitle: t("footprinting:navigation.beef.herdDetails"),
      navigationCategory: t(beefFormCategories.animals),
      key: BeefHerdDetailsForm.Key,
      status: getPageStatus(BeefHerdDetailsForm.Key),
      pageSubtitle: t("beefHerdDetailsForm.subTitle"),
      body: () => <BeefHerdDetailsForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingBeef", "herdDetails"),
    },
    {
      navigationTitle: t("footprinting:navigation.beef.herdOffspring"),
      navigationCategory: t(beefFormCategories.animals),
      key: BeefHerdOffspringForm.Key,
      status: getPageStatus(BeefHerdOffspringForm.Key),
      pageSubtitle: t("beefHerdOffspringForm.subTitle"),
      body: () => <BeefHerdOffspringForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingBeef", "herdOffspring"),
    },
    {
      navigationTitle: t("footprinting:navigation.beef.feedPasture"),
      navigationCategory: t(beefFormCategories.feed),
      key: BeefFeedPastureForm.Key,
      status: getPageStatus(BeefFeedPastureForm.Key),
      pageSubtitle: t("beefFeedPastureForm.subTitle"),
      body: () => <BeefFeedPastureForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingBeef", "feedPasture"),
    },
    {
      navigationTitle: t("footprinting:navigation.beef.feedInformation"),
      navigationCategory: t(beefFormCategories.feed),
      key: BeefFeedInformationForm.Key,
      status: getPageStatus(BeefFeedInformationForm.Key),
      pageSubtitle: t("beefFeedInformationForm.subTitle"),
      body: () => <BeefFeedInformationForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingBeef", "feedInformation"),
    },
    {
      navigationTitle: t("footprinting:navigation.beef.feedAdditives"),
      navigationCategory: t(beefFormCategories.feed),
      key: BeefFeedAdditiveForm.Key,
      status: getPageStatus(BeefFeedAdditiveForm.Key),
      pageSubtitle: t("beefFeedAdditiveForm.subTitle"),
      body: () => <BeefFeedAdditiveForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingBeef", "feedAdditives"),
    },
    {
      navigationTitle: t("footprinting:navigation.beef.feedForageLoss"),
      navigationCategory: t(beefFormCategories.feed),
      key: BeefFeedForageLossForm.Key,
      status: getPageStatus(BeefFeedForageLossForm.Key),
      pageSubtitle: t("beefFeedForageLossForm.subTitle"),
      body: () => <BeefFeedForageLossForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingBeef", "feedForageLoss"),
    },
    {
      navigationTitle: t("footprinting:navigation.beef.manureStorage"),
      navigationCategory: t(beefFormCategories.manure),
      key: BeefManureStorageForm.Key,
      status: getPageStatus(BeefManureStorageForm.Key),
      pageSubtitle: t("beefManureStorageForm.subTitle"),
      body: () => <BeefManureStorageForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingBeef", "manureStorage"),
    },
    {
      navigationTitle: t("footprinting:navigation.beef.manureAdditives"),
      navigationCategory: t(beefFormCategories.manure),
      key: BeefManureAdditiveForm.Key,
      status: getPageStatus(BeefManureAdditiveForm.Key),
      pageSubtitle: t("beefManureAdditiveForm.subTitle"),
      body: () => <BeefManureAdditiveForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingBeef", "manureAdditive"),
    },
    {
      navigationTitle: t("footprinting:navigation.beef.moorland"),
      key: BeefMoorlandForm.Key,
      status: getPageStatus(BeefMoorlandForm.Key),
      pageSubtitle: t("beefMoorlandForm.info"),
      body: () => <BeefMoorlandForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingBeef", "moorland"),
    },
    {
      navigationTitle: t("footprinting:navigation.beef.comment"),
      key: BeefCommentForm.Key,
      status: getPageStatus(BeefCommentForm.Key),
      pageSubtitle: t("footprinting:commentForm.subTitle"),
      body: () => <BeefCommentForm readOnly={readOnly} primaryDataId={beefProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
  ];

  // Carbon programs can opt out of specific steps via excludedFormSteps. Excluded steps
  // are filtered here so they never render, and the server mirrors this by excluding
  // them from completion stats. To exclude a step, add its FormStepId to the carbon
  // program's excludedFormSteps array in consts/carbonPrograms.ts on the API.
  const excludedSteps = new Set(carbonProgramQuery.data?.farmLevelFootprinting.excludedFormSteps ?? []);
  const enabledSteps = beefFlowSteps.filter((step) => !excludedSteps.has(step.key));

  return (
    <FormFlowScreen
      formPages={enabledSteps}
      initialStep={currentStep}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      completionPercent={beefProgressQuery.data?.completionPct ?? 0}
      navBarTitle={t("footprinting:navigation.beef.navBarTitle", { year })}
      editMode={editMode}
      onClose={() => navigate("/footprinting", { state: { year } })}
      isLoading={beefProgressQuery.isLoading}
      enablePageTransitions={enablePageTransitions}
    />
  );
};
